import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { API, useAuth } from '@/App';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { MessageCircle, Send, ArrowLeft, User, Wifi, WifiOff, Trash2, BellOff, Bell } from 'lucide-react';
import { formatMessageTime, formatDaySeparator, sameDay } from '@/lib/messageTime';

// Linkify: makes URLs in message text clickable
const MessageLinkify = ({ text, isOwn }) => {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = text.split(urlRegex);
  return parts.map((part, i) => {
    if (/^https?:\/\//.test(part)) {
      const cleaned = part.replace(/[.,;:!?)]+$/, '');
      const trailing = part.slice(cleaned.length);
      return (
        <span key={i}>
          <a
            href={cleaned}
            target="_blank"
            rel="noopener noreferrer"
            className={`underline break-all ${isOwn ? 'text-white/90 hover:text-white' : 'text-primary hover:text-primary/80'}`}
          >
            {cleaned}
          </a>
          {trailing}
        </span>
      );
    }
    return <span key={i}>{part}</span>;
  });
};

// Get WebSocket URL from API URL
const getWsUrl = () => {
  const apiUrl = API.replace('/api', '');
  return apiUrl.replace('https://', 'wss://').replace('http://', 'ws://');
};

const Messages = () => {
  const { recipientId } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const [typingUser, setTypingUser] = useState(null);
  // Set of user_ids whose thread the viewer has muted. Used to surface a 🔕
  // indicator on the conversation list + header so the muted state is
  // visible at a glance, not just hidden in Settings.
  const [mutedIds, setMutedIds] = useState(new Set());
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  // WebSocket connection
  const connectWebSocket = useCallback(() => {
    if (!token || wsRef.current?.readyState === WebSocket.OPEN) return;

    const wsUrl = `${getWsUrl()}/ws/messages/${token}`;
    const ws = new WebSocket(wsUrl);

    ws.onopen = () => {
      console.log('WebSocket connected');
      setWsConnected(true);
      // Start ping interval
      const pingInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'ping' }));
        }
      }, 30000);
      ws.pingInterval = pingInterval;
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        
        if (data.type === 'new_message') {
          // Add new message to the list if it's for the active conversation
          const msg = data.message;
          if (activeConversation && msg.conversation_id === activeConversation.conversation_id) {
            setMessages(prev => {
              // Avoid duplicates
              if (prev.find(m => m.message_id === msg.message_id)) return prev;
              return [...prev, msg];
            });
          }
          // Refresh conversations list
          fetchConversations();
          
          // Show notification if message is from someone else
          if (msg.sender_id !== user?.user_id) {
            toast.info(`New message from ${msg.sender_name}`);
          }
        } else if (data.type === 'typing') {
          // Show typing indicator
          if (data.conversation_id === activeConversation?.conversation_id) {
            setTypingUser(data.user_id);
            // Clear typing indicator after 3 seconds
            if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
            typingTimeoutRef.current = setTimeout(() => setTypingUser(null), 3000);
          }
        }
      } catch (e) {
        console.error('WebSocket message parse error:', e);
      }
    };

    ws.onclose = () => {
      console.log('WebSocket disconnected');
      setWsConnected(false);
      if (ws.pingInterval) clearInterval(ws.pingInterval);
      // Attempt to reconnect after 5 seconds
      reconnectTimeoutRef.current = setTimeout(connectWebSocket, 5000);
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };

    wsRef.current = ws;
  }, [token, activeConversation, user]);

  // Connect WebSocket when user is authenticated
  useEffect(() => {
    if (user && token) {
      connectWebSocket();
    }
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, [user, token, connectWebSocket]);

  useEffect(() => {
    if (user) {
      fetchConversations();
      if (recipientId) {
        openConversation(recipientId);
      }
    }
  }, [user, recipientId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchConversations = async () => {
    try {
      const response = await axios.get(`${API}/messages/conversations`);
      setConversations(response.data);
    } catch (error) {
      console.error('Failed to fetch conversations');
    } finally {
      setLoading(false);
    }
  };

  const fetchMuted = async () => {
    try {
      const res = await axios.get(`${API}/messages/mute`);
      setMutedIds(new Set((res.data?.muted || []).map((u) => u.user_id)));
    } catch {
      /* If endpoint fails, just don't surface the indicator. */
    }
  };

  // Refresh muted list whenever we land on the page or open a conversation.
  useEffect(() => {
    if (user) fetchMuted();
  }, [user]);

  const toggleMute = async (otherUserId) => {
    if (!otherUserId) return;
    const isMuted = mutedIds.has(otherUserId);
    // Optimistic update for snappy UI
    setMutedIds((prev) => {
      const next = new Set(prev);
      if (isMuted) next.delete(otherUserId);
      else next.add(otherUserId);
      return next;
    });
    try {
      if (isMuted) {
        await axios.delete(`${API}/messages/mute/${otherUserId}`);
        toast.success('Thread un-muted');
      } else {
        await axios.post(`${API}/messages/mute/${otherUserId}`);
        toast.success('Thread muted');
      }
    } catch (e) {
      // Roll back
      setMutedIds((prev) => {
        const next = new Set(prev);
        if (isMuted) next.add(otherUserId);
        else next.delete(otherUserId);
        return next;
      });
      toast.error('Could not update mute state');
    }
  };

  const openConversation = async (userId) => {
    try {
      const convResponse = await axios.get(`${API}/messages/conversation/${userId}`);
      setActiveConversation(convResponse.data);
      
      const msgResponse = await axios.get(`${API}/messages/${convResponse.data.conversation_id}`);
      setMessages(msgResponse.data);
      
      // Refresh conversations to update unread counts
      fetchConversations();
      // Tell Navbar to refresh message badge
      window.dispatchEvent(new CustomEvent('notifications-updated'));
    } catch (error) {
      toast.error('Failed to open conversation');
    }
  };

  // Send typing indicator
  const sendTypingIndicator = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN && activeConversation) {
      wsRef.current.send(JSON.stringify({
        type: 'typing',
        conversation_id: activeConversation.conversation_id
      }));
    }
  }, [activeConversation]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !activeConversation) return;

    setSending(true);
    try {
      const recipientId = activeConversation.participants.find(p => p !== user.user_id);
      await axios.post(`${API}/messages/send`, {
        recipient_id: recipientId,
        content: newMessage.trim()
      });
      
      setNewMessage('');
      // Reset textarea height
      const textarea = document.querySelector('[data-testid="message-input"]');
      if (textarea) textarea.style.height = 'auto';
      
      // Message will be added via WebSocket, but fallback to fetch if WS not connected
      if (!wsConnected) {
        const msgResponse = await axios.get(`${API}/messages/${activeConversation.conversation_id}`);
        setMessages(msgResponse.data);
        fetchConversations();
      }
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const getOtherParticipant = (conv) => {
    if (!conv || !user) return null;
    const otherId = conv.participants.find(p => p !== user.user_id);
    return {
      id: otherId,
      name: conv.participant_names?.[otherId] || conv.other_user?.name || 'Unknown',
      profile_picture: conv.other_user?.profile_picture
    };
  };

  const handleDeleteConversation = async (conversationId, e) => {
    e.stopPropagation(); // Prevent opening the conversation
    
    if (!window.confirm('Are you sure you want to delete this conversation? This action cannot be undone.')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/messages/conversations/${conversationId}`);
      toast.success('Conversation deleted');
      
      // Update local state
      setConversations(prev => prev.filter(c => c.conversation_id !== conversationId));
      
      // If the deleted conversation was active, clear it
      if (activeConversation?.conversation_id === conversationId) {
        setActiveConversation(null);
        setMessages([]);
      }
    } catch (error) {
      toast.error('Failed to delete conversation');
      console.error('Delete error:', error);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="pt-24 text-center">
          <p>Please sign in to view messages</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <div className="pt-20 pb-4 px-4 sm:px-6 lg:px-8 h-screen">
        <div className="max-w-6xl mx-auto h-full">
          <div className="bg-white rounded-3xl border-2 border-border overflow-hidden h-[calc(100vh-120px)] flex">
            {/* Conversations List */}
            <div className={`w-full md:w-80 border-r border-border flex flex-col ${activeConversation ? 'hidden md:flex' : 'flex'}`}>
              <div className="p-4 border-b border-border">
                <h2 className="text-xl font-heading font-bold flex items-center gap-2">
                  <MessageCircle className="w-5 h-5" />
                  Messages
                </h2>
              </div>
              
              <div className="flex-1 overflow-y-auto">
                {loading ? (
                  <div className="p-4 text-center text-muted-foreground">Loading...</div>
                ) : conversations.length === 0 ? (
                  <div className="p-8 text-center text-muted-foreground">
                    <MessageCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No messages yet</p>
                    <p className="text-sm mt-2">Start a conversation from a user's profile</p>
                  </div>
                ) : (
                  conversations.map((conv) => {
                    const other = getOtherParticipant(conv);
                    const isActive = activeConversation?.conversation_id === conv.conversation_id;
                    
                    return (
                      <div
                        key={conv.conversation_id}
                        onClick={() => openConversation(other?.id)}
                        className={`p-4 cursor-pointer hover:bg-muted/50 border-b border-border/50 ${isActive ? 'bg-primary/5' : ''} group`}
                        data-testid={`conversation-item-${conv.conversation_id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-gradient-to-br from-primary to-accent rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
                            {other?.profile_picture ? (
                              <img src={other.profile_picture} alt="" className="w-full h-full rounded-full object-cover" />
                            ) : (
                              other?.name?.charAt(0) || '?'
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <p
                                className="font-semibold truncate flex items-center gap-1.5"
                                data-testid={`conv-name-${conv.conversation_id}`}
                              >
                                {other?.name}
                                {other?.id && mutedIds.has(other.id) && (
                                  <BellOff
                                    className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0"
                                    aria-label="Muted"
                                    data-testid={`conv-muted-indicator-${conv.conversation_id}`}
                                  />
                                )}
                              </p>
                              <div className="flex items-center gap-2">
                                {conv.unread_count > 0 && (
                                  <span className="bg-primary text-white text-xs px-2 py-0.5 rounded-full">
                                    {conv.unread_count}
                                  </span>
                                )}
                                <button
                                  onClick={(e) => handleDeleteConversation(conv.conversation_id, e)}
                                  className="opacity-0 group-hover:opacity-100 p-1.5 rounded-full hover:bg-red-100 text-slate-400 hover:text-red-500 transition-all"
                                  data-testid={`delete-conversation-${conv.conversation_id}`}
                                  title="Delete conversation"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                            <p className="text-sm text-muted-foreground truncate">{conv.last_message || 'No messages yet'}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Messages Area — min-w-0 is critical: without it, this flex
                item can't shrink below the intrinsic width of its content
                (e.g. a wide message bubble or the composer form), which
                pushes the Send button off-screen on narrow viewports. */}
            <div className={`flex-1 min-w-0 flex flex-col ${!activeConversation ? 'hidden md:flex' : 'flex'}`}>
              {activeConversation ? (
                <>
                  {/* Header */}
                  <div className="p-4 border-b border-border flex items-center gap-3">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="md:hidden"
                      onClick={() => setActiveConversation(null)}
                    >
                      <ArrowLeft className="w-5 h-5" />
                    </Button>
                    <div className="w-10 h-10 bg-gradient-to-br from-primary to-accent rounded-full flex items-center justify-center text-white font-bold">
                      {getOtherParticipant(activeConversation)?.name?.charAt(0) || '?'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p
                        className="font-semibold flex items-center gap-1.5 truncate"
                        data-testid="active-conversation-name"
                      >
                        {getOtherParticipant(activeConversation)?.name}
                        {(() => {
                          const oid = getOtherParticipant(activeConversation)?.id;
                          return oid && mutedIds.has(oid) ? (
                            <BellOff
                              className="w-4 h-4 text-muted-foreground flex-shrink-0"
                              aria-label="Muted"
                              data-testid="active-conv-muted-indicator"
                            />
                          ) : null;
                        })()}
                      </p>
                      <div className="flex items-center gap-1 text-xs">
                        {wsConnected ? (
                          <span className="text-green-500 flex items-center gap-1">
                            <Wifi className="w-3 h-3" /> Online
                          </span>
                        ) : (
                          <span className="text-slate-400 flex items-center gap-1">
                            <WifiOff className="w-3 h-3" /> Offline
                          </span>
                        )}
                      </div>
                    </div>
                    {(() => {
                      const oid = getOtherParticipant(activeConversation)?.id;
                      if (!oid) return null;
                      const isMuted = mutedIds.has(oid);
                      return (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleMute(oid)}
                          className="rounded-full"
                          title={isMuted ? 'Un-mute this thread' : 'Mute this thread'}
                          data-testid="toggle-mute-btn"
                        >
                          {isMuted ? <Bell className="w-4 h-4" /> : <BellOff className="w-4 h-4" />}
                        </Button>
                      );
                    })()}
                    <Link to={`/profile/${getOtherParticipant(activeConversation)?.id}`} className="ml-auto">
                      <Button variant="outline" size="sm">View Profile</Button>
                    </Link>
                  </div>

                  {/* Messages — overflow-x-hidden prevents wide message
                      content (long URLs, code blocks) from making the
                      scroll container horizontally overflow and pushing
                      the composer's Send button off-screen. */}
                  <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 space-y-4">
                    {messages.map((msg, idx) => {
                      const isOwn = msg.sender_id === user.user_id;
                      const prev = messages[idx - 1];
                      const showDaySeparator = !prev || !sameDay(prev.created_at, msg.created_at);
                      return (
                        <div key={msg.message_id}>
                          {showDaySeparator && (
                            <div
                              className="flex justify-center my-3"
                              data-testid={`message-day-separator-${msg.message_id}`}
                            >
                              <span className="px-3 py-1 rounded-full bg-muted text-xs font-medium text-muted-foreground">
                                {formatDaySeparator(msg.created_at)}
                              </span>
                            </div>
                          )}
                          <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
                            <div className={`max-w-[70%] px-4 py-3 rounded-2xl ${
                              isOwn
                                ? 'bg-primary text-white rounded-br-md'
                                : 'bg-muted rounded-bl-md'
                            }`}>
                              {msg.context && (
                                <div className={`text-xs mb-2 pb-2 border-b ${isOwn ? 'border-white/20 text-white/80' : 'border-slate-200 text-slate-500'}`}>
                                  Regarding: {msg.context.title}
                                </div>
                              )}
                              <p className="whitespace-pre-wrap select-text" style={{ WebkitUserSelect: 'text', userSelect: 'text', WebkitTouchCallout: 'default', overflowWrap: 'anywhere', wordBreak: 'break-word' }}><MessageLinkify text={msg.content} isOwn={isOwn} /></p>
                              <p
                                className={`text-xs mt-1 ${isOwn ? 'text-white/70' : 'text-muted-foreground'}`}
                                title={new Date(msg.created_at).toLocaleString()}
                              >
                                {formatMessageTime(msg.created_at)}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    {/* Typing Indicator */}
                    {typingUser && (
                      <div className="flex justify-start">
                        <div className="bg-muted px-4 py-3 rounded-2xl rounded-bl-md">
                          <div className="flex gap-1">
                            <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{animationDelay: '0ms'}}></span>
                            <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{animationDelay: '150ms'}}></span>
                            <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{animationDelay: '300ms'}}></span>
                          </div>
                        </div>
                      </div>
                    )}
                    <div ref={messagesEndRef} />
                  </div>

                  {/* Input */}
                  <form onSubmit={handleSendMessage} className="p-3 sm:p-4 border-t border-border">
                    <div className="flex gap-2 items-end w-full min-w-0">
                      <textarea
                        value={newMessage}
                        onChange={(e) => {
                          setNewMessage(e.target.value);
                          sendTypingIndicator();
                          // Auto-grow textarea
                          e.target.style.height = 'auto';
                          e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
                        }}
                        onKeyDown={(e) => {
                          // Ctrl+Enter or Cmd+Enter sends the message
                          if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                            e.preventDefault();
                            if (newMessage.trim() && !sending) {
                              handleSendMessage(e);
                            }
                          }
                        }}
                        placeholder="Type a message..."
                        // min-w-0 is required for flex-1 to shrink below the
                        // textarea's intrinsic width on narrow viewports.
                        // Without it, the send button gets pushed off-screen
                        // on some Android devices when the keyboard is open.
                        className="flex-1 min-w-0 min-h-[48px] max-h-[120px] py-3 px-4 rounded-2xl border border-input bg-background text-sm resize-none focus:outline-none focus:ring-1 focus:ring-ring"
                        disabled={sending}
                        rows={1}
                        data-testid="message-input"
                      />
                      <Button
                        type="submit"
                        disabled={!newMessage.trim() || sending}
                        className="h-12 w-12 rounded-full bg-primary flex-shrink-0"
                        data-testid="send-message-btn"
                      >
                        <Send className="w-5 h-5" />
                      </Button>
                    </div>
                  </form>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center text-muted-foreground">
                  <div className="text-center">
                    <MessageCircle className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p className="text-lg font-medium">Select a conversation</p>
                    <p className="text-sm">or start a new one from a user's profile</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Messages;
